/* ===== «Замечания» — the pure functions (group E, P13) =====
 *
 * One set of functions for the client and the server: the simple checks (no model) are computed from the
 * campaign's data on the page and on the server with the same code, so a key means the same thing on both sides
 * («left as is» is stored by the server under the key the client shows). Deterministic: the same data gives the
 * same keys in the same order.
 *
 *   findSimpleRemarks — duplicate / empty prep / empty NPC / broken link / field misuse
 *   fingerprintOf     — a hash of what the remark's evidence holds now (name + data): a remark the GM left as is
 *                       comes back only when it changes
 *   visibleRemarks    — what the panel shows, by the campaign's switches and what is left as is
 */
import type {
  Beat, CharacterSheet, JournalEntry, JournalQuestData, Remark, RemarkLink, RemarkSettings, RemarksState, TimelineEvent,
} from './domain.js';

export const DEFAULT_REMARK_SETTINGS: RemarkSettings = { gameHints: true, prepHints: true, offerFixes: true, simpleChecks: true };

/** The switches with the defaults for whatever is missing. */
export function resolveRemarkSettings(s?: Partial<RemarkSettings> | null): RemarkSettings {
  return {
    gameHints: s?.gameHints ?? true,
    prepHints: s?.prepHints ?? true,
    offerFixes: s?.offerFixes ?? true,
    simpleChecks: s?.simpleChecks ?? true,
  };
}

export interface RemarkInput {
  beats: Beat[];
  journal: JournalEntry[];
  sheets: CharacterSheet[];
  pages: Array<{ id: string; name: string }>;
  timeline?: TimelineEvent[];
}

/** «Победа над…», «Если…», «When…» — a condition, not a reward. Letters of any alphabet end the word. */
export const REWARD_CONDITION_RE = /^(победа|после|когда|если|victory|after|when|if)(?![\p{L}\p{N}_])/iu;

const beatName = (b: Pick<Beat, 'sessionLabel' | 'order'>): string => b.sessionLabel ?? `Beat ${b.order}`;
const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** The text of a record the duplicate check reads: the description of a quest / NPC / place / faction, the text of a note. */
export function entryText(e: Pick<JournalEntry, 'type' | 'data'>): string {
  const d = (e.data ?? {}) as unknown as Record<string, unknown>;
  switch (e.type) {
    case 'quest': case 'npc': case 'location': case 'faction': return str(d.description);
    default: return str(d.content);
  }
}

const STRUCTURED = new Set(['quest', 'npc', 'location', 'faction']);

function stable(v: unknown): string {
  if (v === undefined || v === null) return 'null';
  if (typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o).sort().filter(k => o[k] !== undefined).map(k => `${JSON.stringify(k)}:${stable(o[k])}`).join(',')}}`;
}

/** cyrb53 — a small string hash (the client and the server both run it; no crypto needed). */
function hash(text: string): string {
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < text.length; i++) {
    const ch = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16);
}

/** Simple checks, in a fixed order: duplicates, field misuse, empty prep, empty NPCs, broken links. */
export function findSimpleRemarks(input: RemarkInput): Remark[] {
  const out: Remark[] = [];
  const journal = input.journal;
  const byId = new Map(journal.map(e => [e.id, e]));
  const sheetById = new Map(input.sheets.map(s => [s.id, s]));
  const link = (e: JournalEntry): RemarkLink => ({ kind: 'journal', id: e.id, name: e.name });

  // duplicate: the text of a record is the text of another one, word for word (the edges' blanks do not count)
  const groups = new Map<string, JournalEntry[]>();
  for (const e of journal) {
    const t = entryText(e).trim();
    if (t === '') continue;
    const g = groups.get(t);
    if (g === undefined) groups.set(t, [e]); else g.push(e);
  }
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    // the original: a structured record (quest, NPC…) over a note; then the older one; then by id
    const sorted = [...group].sort((a, b) =>
      (STRUCTURED.has(a.type) ? 0 : 1) - (STRUCTURED.has(b.type) ? 0 : 1)
      || str(a.createdAt).localeCompare(str(b.createdAt))
      || a.id.localeCompare(b.id));
    const original = sorted[0]!;
    for (const copy of sorted.slice(1)) {
      out.push({
        key: `duplicate:${copy.id},${original.id}`, kind: 'simple', check: 'duplicate', title: '', body: '',
        links: [link(copy), link(original)],
        fix: { label: 'Delete the record', confirm: 'delete' },
      });
    }
  }

  // field misuse: the rewards of a quest begin with a condition
  for (const e of journal) {
    if (e.type !== 'quest') continue;
    const rewards = str((e.data as JournalQuestData | undefined)?.rewards).trim();
    if (rewards !== '' && REWARD_CONDITION_RE.test(rewards)) {
      out.push({ key: `field-misuse:${e.id}`, kind: 'simple', check: 'field-misuse', title: '', body: '', links: [link(e)], fix: { label: 'Move to the description' } });
    }
  }

  // empty prep: a beat (a reserve one too) with no prep record, or with an empty one
  for (const b of input.beats) {
    const prep = b.journalEntryId ? byId.get(b.journalEntryId) : undefined;
    if (prep !== undefined && entryText(prep).trim() !== '') continue;
    out.push({
      key: `empty-prep:${b.id}`, kind: 'simple', check: 'empty-prep', title: '', body: '',
      links: [{ kind: 'beat', id: b.id, name: beatName(b) }], fix: { label: 'Draft the prep' },
    });
  }

  // empty NPC: an NPC sheet among a beat's materials with no description
  const seenNpc = new Set<string>();
  for (const b of input.beats) {
    for (const m of b.materials ?? []) {
      if (m.kind !== 'sheet' || seenNpc.has(m.id)) continue;
      const sheet = sheetById.get(m.id);
      if (sheet === undefined || sheet.sheetType !== 'npc') continue;
      if (str(sheet.values?.description).trim() !== '' || str(sheet.tokenDescription).trim() !== '' || str(sheet.values?.tokenDescription).trim() !== '') continue;
      seenNpc.add(m.id);
      out.push({
        key: `empty-npc:${sheet.id}`, kind: 'simple', check: 'empty-npc', title: '', body: '',
        links: [{ kind: 'sheet', id: sheet.id, name: sheet.name }, { kind: 'beat', id: b.id, name: beatName(b) }],
        fix: { label: 'Write a description' },
      });
    }
  }

  // broken link: a beat material, a quest's «follows from», a chronicle event's link to something deleted
  for (const b of input.beats) {
    for (const m of b.materials ?? []) {
      const there = m.kind === 'journal' ? byId.has(m.id) : sheetById.has(m.id);
      if (there) continue;
      out.push({
        key: `broken-link:${b.id},${m.id}`, kind: 'simple', check: 'broken-link', title: '', body: '',
        links: [{ kind: 'beat', id: b.id, name: beatName(b) }], fix: { label: 'Remove the link' },
      });
    }
  }
  for (const e of journal) {
    if (e.type !== 'quest') continue;
    const from = (e.data as JournalQuestData | undefined)?.followsFrom;
    if (from === undefined || from === null || byId.has(from.questId)) continue;
    out.push({
      key: `broken-link:${e.id},${from.questId}`, kind: 'simple', check: 'broken-link', title: '', body: '',
      links: [link(e)], fix: { label: 'Remove the link' },
    });
  }
  for (const ev of input.timeline ?? []) {
    for (const l of ev.links ?? []) {
      const target = l.targetType as string;
      const there = target === 'journal' ? byId.has(l.targetId) : target === 'sheet' ? sheetById.has(l.targetId) : true;
      if (there) continue;
      out.push({
        key: `broken-link:${ev.id},${l.targetId}`, kind: 'simple', check: 'broken-link', title: '', body: ev.title,
        links: [], fix: { label: 'Remove the link' },
      });
    }
  }
  return out;
}

/** What one link's thing holds now — «gone» if it is not there. */
function linkState(l: RemarkLink, input: RemarkInput): string {
  switch (l.kind) {
    case 'journal': {
      const e = input.journal.find(x => x.id === l.id);
      return e === undefined ? 'gone' : stable({ n: e.name, t: e.type, d: e.data, v: e.visibility });
    }
    case 'beat': {
      const b = input.beats.find(x => x.id === l.id);
      return b === undefined ? 'gone' : stable({ l: b.sessionLabel, s: b.status, j: b.journalEntryId, m: b.materials, o: (b.outcomes ?? []).map(o => [o.label, o.toBeatId]) });
    }
    case 'sheet': {
      const s = input.sheets.find(x => x.id === l.id);
      return s === undefined ? 'gone' : stable({ n: s.name, v: s.values, d: s.tokenDescription });
    }
    case 'page': {
      const p = input.pages.find(x => x.id === l.id);
      return p === undefined ? 'gone' : stable({ n: p.name });
    }
  }
}

/**
 * A hash of the content of the records the remark names (name + data), not of their update times: an edit that
 * returns a record to what it was returns the same hash. A remark with no links (a broken link of a chronicle event)
 * hashes its own subject text.
 */
export function fingerprintOf(remark: Remark, input: RemarkInput): string {
  const parts = remark.links.map(l => `${l.kind}:${l.id}=${linkState(l, input)}`);
  if (remark.links.length === 0) parts.push(`own:${remark.title}|${remark.body}`);
  return hash(parts.join('\n'));
}

export interface RemarksView {
  /** Game hints that are not stale (the table shows the newest of them; the panel shows all). */
  game: Remark[];
  /** Game hints out of date: a collapsed line without buttons. */
  staleGame: Remark[];
  /** Simple + the model's prep remarks, not left as is. */
  open: Remark[];
  /** How many remarks the GM left as is (and nothing changed in their records since). */
  leftAsIs: number;
}

/** What the panel shows: by the campaign's four switches, minus what the GM left as is. */
export function visibleRemarks(input: RemarkInput, state?: Pick<RemarksState, 'settings' | 'model' | 'dismissed'> | null): RemarksView {
  const settings = resolveRemarkSettings(state?.settings);
  const dismissed = new Map((state?.dismissed ?? []).map(d => [d.key, d.fingerprint]));
  const simple = settings.simpleChecks ? findSimpleRemarks(input) : [];
  const model = (state?.model ?? []).filter(r => (r.kind === 'game' ? settings.gameHints : r.kind === 'prep' ? settings.prepHints : false));
  const view: RemarksView = { game: [], staleGame: [], open: [], leftAsIs: 0 };
  for (const r of [...simple, ...model]) {
    const fp = dismissed.get(r.key);
    if (fp !== undefined && fp === fingerprintOf(r, input)) { view.leftAsIs += 1; continue; }
    if (r.kind === 'game') (r.stale === true ? view.staleGame : view.game).push(r);
    else view.open.push(r);
  }
  return view;
}

/** The number on the rail item: open remarks + hints that are not out of date. */
export function remarksCount(view: RemarksView): number {
  return view.open.length + view.game.length;
}

/** The things open in Studio that this remark names (for the line «N remarks — open» on a thing). */
export function remarksNaming(view: RemarksView, kind: RemarkLink['kind'], id: string): number {
  let n = 0;
  for (const r of [...view.open, ...view.game]) if (r.links.some(l => l.kind === kind && l.id === id)) n += 1;
  return n;
}
